import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";

import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Badge,
	Box,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Checkbox,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Icon,
	Input,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Select,
	SimpleGrid,
	Spinner,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiDownload, FiEye, FiTrash2, FiUploadCloud } from "react-icons/fi";

import { PropertyConfirmModal } from "../../Components/PropertyModal/shared";
import {
	MAX_BULK_ROWS,
	buildBatchPairIndex,
	canGenerateReviewRows,
	filterPaymentRows,
	flattenTemplatePayments,
	formatBatchBadgeDate,
	formatCents,
	providersWithIban,
	recipientsWithValidIban,
	sortPaymentRows,
	sumSelectedCents,
} from "../../features/bulkPayments/bulkPaymentsSelectors";
import {
	clearSelectedBatch,
	deleteBulkPaymentAsync,
	generateBulkPaymentAsync,
	getBulkPaymentAsync,
	getBulkPaymentFileAsync,
	listBulkPaymentsAsync,
} from "../../redux/bulkPayments/bulkPaymentsSlice";
import { selectActiveProperties } from "../../redux/user/userSlice";
import { isValidIban } from "../../utils/verifier";

const STEPS = ["template", "select", "review"];

export default function BulkPayments() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const toast = useToast();
	const textColor = useColorModeValue("gray.700", "white");
	const user = useSelector((state) => state.user.user);
	const properties = useSelector(selectActiveProperties);
	const batches = useSelector((state) => state.bulkPayments.batches);
	const selectedBatch = useSelector((state) => state.bulkPayments.selectedBatch);
	const listStatus = useSelector((state) => state.bulkPayments.status);
	const detailStatus = useSelector((state) => state.bulkPayments.detailStatus);
	const mutationStatus = useSelector((state) => state.bulkPayments.mutationStatus);

	const [step, setStep] = useState(0);
	const [template, setTemplate] = useState("electricity");
	const [providerId, setProviderId] = useState("");
	const [providerError, setProviderError] = useState("");
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("all");
	const [dueFrom, setDueFrom] = useState(() => moment().startOf("month").format("YYYY-MM-DD"));
	const [dueTo, setDueTo] = useState(() => moment().endOf("month").format("YYYY-MM-DD"));
	const [selectableOnly, setSelectableOnly] = useState(true);
	const [hideInBatch, setHideInBatch] = useState(true);
	const [selectedKeys, setSelectedKeys] = useState(() => new Set());
	const [reviewRows, setReviewRows] = useState([]);
	const [rowErrors, setRowErrors] = useState({});
	const [batchToDelete, setBatchToDelete] = useState(null);

	const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

	const agencyIbanValid = isValidIban(user?.billingInformation?.iban);
	const providers = providersWithIban(user?.providers || []);
	const recipientOptions = useMemo(
		() => recipientsWithValidIban(user?.associations || [], user?.providers || []),
		[user?.associations, user?.providers]
	);
	const pairIndex = useMemo(() => buildBatchPairIndex(batches), [batches]);
	const tableHeaderBg = useColorModeValue("white", "gray.800");

	const allRows = useMemo(() => flattenTemplatePayments(properties, template), [properties, template]);
	const baseFilteredRows = useMemo(
		() => filterPaymentRows(allRows, { search, status, dueFrom, dueTo }),
		[allRows, search, status, dueFrom, dueTo]
	);
	const filteredRows = useMemo(
		() =>
			sortPaymentRows(
				filterPaymentRows(allRows, {
					search,
					status,
					dueFrom,
					dueTo,
					selectableOnly,
					hideInBatch,
					pairIndex,
				})
			),
		[allRows, search, status, dueFrom, dueTo, selectableOnly, hideInBatch, pairIndex]
	);
	const hiddenByDeclutter = Math.max(0, baseFilteredRows.length - filteredRows.length);
	const selectableFiltered = filteredRows.filter((row) => row.selectable);
	const selectedCount = selectedKeys.size;
	const selectedTotal = formatCents(sumSelectedCents(allRows, selectedKeys));

	useEffect(() => {
		dispatch(listBulkPaymentsAsync()).then((result) => {
			if (result.error) {
				toast({
					title: t("bulkPayments.loadError"),
					description: result.payload?.message || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			}
		});
	}, [dispatch, t, toast]);

	useEffect(() => {
		setSelectedKeys(new Set());
		setReviewRows([]);
		setRowErrors({});
		setProviderId("");
		setProviderError("");
		setStep(0);
	}, [template]);

	const selectedProvider = providers.find((provider) => provider.id === providerId);

	const validateProvider = (id) => {
		if (template === "associations") {
			setProviderError("");
			return true;
		}
		if (!id) {
			setProviderError(t("bulkPayments.providerRequired"));
			return false;
		}
		const provider = providers.find((entry) => entry.id === id);
		if (!provider || !isValidIban(provider.iban)) {
			setProviderError(t("bulkPayments.providerInvalidIban"));
			return false;
		}
		setProviderError("");
		return true;
	};

	const goToSelect = () => {
		if (!validateProvider(providerId)) return;
		setStep(1);
	};

	const toggleRow = (row) => {
		if (!row.selectable) return;
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(row.key)) {
				next.delete(row.key);
			} else {
				if (next.size >= MAX_BULK_ROWS) {
					toast({
						title: t("bulkPayments.rowCapReached", { count: MAX_BULK_ROWS }),
						status: "warning",
						duration: 3000,
						isClosable: true,
					});
					return prev;
				}
				next.add(row.key);
			}
			return next;
		});
	};

	const toggleSelectAll = () => {
		const allSelected = selectableFiltered.every((row) => selectedKeys.has(row.key)) && selectableFiltered.length > 0;
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				selectableFiltered.forEach((row) => next.delete(row.key));
				return next;
			}
			for (const row of selectableFiltered) {
				if (next.size >= MAX_BULK_ROWS) {
					toast({
						title: t("bulkPayments.rowCapReached", { count: MAX_BULK_ROWS }),
						status: "warning",
						duration: 3000,
						isClosable: true,
					});
					break;
				}
				next.add(row.key);
			}
			return next;
		});
	};

	const goToReview = () => {
		const selected = allRows.filter((row) => selectedKeys.has(row.key) && row.selectable);
		if (!selected.length) {
			toast({ title: t("bulkPayments.selectAtLeastOne"), status: "warning", duration: 3000, isClosable: true });
			return;
		}
		setReviewRows(
			selected.map((row) => ({
				...row,
				details1: row.defaults.details1,
				details2: row.defaults.details2,
				recipient: row.defaultRecipient,
			}))
		);
		setRowErrors({});
		setStep(2);
	};

	const updateReviewRow = (key, patch) => {
		setReviewRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
		setRowErrors((prev) => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const handleGenerate = () => {
		if (!validateProvider(providerId)) {
			setStep(0);
			return;
		}
		if (!canGenerateReviewRows(template, reviewRows)) {
			toast({ title: t("bulkPayments.fillRequiredFields"), status: "warning", duration: 3000, isClosable: true });
			return;
		}

		const payload = {
			template,
			providerId: template === "associations" ? undefined : providerId,
			rows: reviewRows.map((row) => ({
				propertyId: row.propertyId,
				paymentId: row.paymentId,
				details1: row.details1,
				details2: row.details2,
				...(template === "associations" ? { recipient: row.recipient } : {}),
			})),
		};

		dispatch(generateBulkPaymentAsync(payload)).then((result) => {
			if (result.error) {
				const error = result.payload || {};
				if (Array.isArray(error.rows) && error.rows.length) {
					const nextErrors = {};
					for (const row of error.rows) {
						nextErrors[`${row.propertyId}#${row.paymentId}`] = row.reason;
					}
					setRowErrors(nextErrors);
				}
				toast({
					title: t("bulkPayments.generateError"),
					description: error.message || result.error.message,
					status: "error",
					duration: 7000,
					isClosable: true,
				});
				return;
			}
			if (result.payload?.url) {
				window.open(result.payload.url, "_blank", "noopener,noreferrer");
			}
			dispatch(listBulkPaymentsAsync());
			toast({ title: t("bulkPayments.generateSuccess"), status: "success", duration: 3000, isClosable: true });
			setSelectedKeys(new Set());
			setReviewRows([]);
			setStep(0);
		});
	};

	const openDownload = (batchId) => {
		dispatch(getBulkPaymentFileAsync(batchId)).then((result) => {
			if (result.error) {
				toast({
					title: t("bulkPayments.downloadError"),
					description: result.payload?.message || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
				return;
			}
			if (result.payload?.url) window.open(result.payload.url, "_blank", "noopener,noreferrer");
		});
	};

	const openView = (batchId) => {
		dispatch(getBulkPaymentAsync(batchId)).then((result) => {
			if (result.error) {
				toast({
					title: t("bulkPayments.loadError"),
					description: result.payload?.message || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
				return;
			}
			onViewOpen();
		});
	};

	const confirmDelete = () => {
		if (!batchToDelete) return;
		dispatch(deleteBulkPaymentAsync(batchToDelete)).then((result) => {
			if (result.error) {
				toast({
					title: t("bulkPayments.deleteError"),
					description: result.payload?.message || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			} else {
				toast({ title: t("bulkPayments.deleteSuccess"), status: "success", duration: 2500, isClosable: true });
			}
			setBatchToDelete(null);
			onDeleteClose();
		});
	};

	if (!agencyIbanValid) {
		return (
			<Flex direction="column">
				<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
					<CardHeader>
						<HStack spacing={3}>
							<Icon as={FiUploadCloud} w={6} h={6} color="blue.500" />
							<Text fontSize="2xl" color={textColor} fontWeight="bold">
								{t("bulkPayments.title")}
							</Text>
						</HStack>
					</CardHeader>
					<CardBody>
						<Alert status="warning" borderRadius="md">
							<AlertIcon />
							<Box>
								<AlertTitle>{t("bulkPayments.agencyIbanRequiredTitle")}</AlertTitle>
								<AlertDescription>
									{user?.type === "agent" ? t("bulkPayments.agencyIbanRequiredAgent") : t("bulkPayments.agencyIbanRequiredAgency")}{" "}
									<Link as={RouterLink} to="/profile" color="blue.600" fontWeight="semibold">
										{t("bulkPayments.goToProfile")}
									</Link>
								</AlertDescription>
							</Box>
						</Alert>
					</CardBody>
				</Card>
			</Flex>
		);
	}

	const allSelectableChecked = selectableFiltered.length > 0 && selectableFiltered.every((row) => selectedKeys.has(row.key));

	return (
		<Flex direction="column">
			<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
				<CardHeader>
					<HStack spacing={3} align="start">
						<Icon as={FiUploadCloud} w={6} h={6} color="blue.500" mt={1} />
						<Box>
							<Text fontSize="2xl" color={textColor} fontWeight="bold">
								{t("bulkPayments.title")}
							</Text>
							<Text color="gray.600" fontSize="sm">
								{t("bulkPayments.subtitle")}
							</Text>
						</Box>
					</HStack>
				</CardHeader>
				<CardBody>
			<Tabs variant="enclosed" colorScheme="blue">
				<TabList>
					<Tab>{t("bulkPayments.generateTab")}</Tab>
					<Tab>
						{t("bulkPayments.historyTab")}
						{listStatus === "loading" ? <Spinner size="xs" ml={2} /> : null}
					</Tab>
				</TabList>
				<TabPanels>
					<TabPanel px={0}>
						<HStack mb={4} spacing={2} flexWrap="wrap">
							{STEPS.map((id, index) => (
								<Badge key={id} colorScheme={step === index ? "blue" : step > index ? "green" : "gray"} px={3} py={1}>
									{index + 1}. {t(`bulkPayments.steps.${id}`)}
								</Badge>
							))}
						</HStack>

						{step === 0 && (
							<VStack align="stretch" spacing={4} maxW="640px">
								<FormControl>
									<FormLabel>{t("bulkPayments.template")}</FormLabel>
									<Select value={template} onChange={(e) => setTemplate(e.target.value)}>
										<option value="electricity">{t("bulkPayments.templates.electricity")}</option>
										<option value="gas">{t("bulkPayments.templates.gas")}</option>
										<option value="associations">{t("bulkPayments.templates.associations")}</option>
									</Select>
								</FormControl>

								{(template === "electricity" || template === "gas") && (
									<FormControl isInvalid={Boolean(providerError)} isRequired>
										<FormLabel>{t("bulkPayments.provider")}</FormLabel>
										{providers.length === 0 ? (
											<Alert status="info" borderRadius="md">
												<AlertIcon />
												<AlertDescription>
													{t("bulkPayments.noProviders")}{" "}
													<Link as={RouterLink} to="/profile" color="blue.600" fontWeight="semibold">
														{t("bulkPayments.goToProviders")}
													</Link>
												</AlertDescription>
											</Alert>
										) : (
											<Select
												placeholder={t("bulkPayments.selectProvider")}
												value={providerId}
												onChange={(e) => {
													setProviderId(e.target.value);
													validateProvider(e.target.value);
												}}
											>
												{providers.map((provider) => (
													<option key={provider.id} value={provider.id}>
														{provider.name}
														{isValidIban(provider.iban) ? "" : ` (${t("bulkPayments.invalidIbanShort")})`}
													</option>
												))}
											</Select>
										)}
										<FormErrorMessage>{providerError}</FormErrorMessage>
									</FormControl>
								)}

								<Button colorScheme="blue" onClick={goToSelect} isDisabled={template !== "associations" && providers.length === 0}>
									{t("bulkPayments.next")}
								</Button>
							</VStack>
						)}

						{step === 1 && (
							<VStack align="stretch" spacing={4}>
								<SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
									<FormControl>
										<FormLabel fontSize="sm">{t("bulkPayments.search")}</FormLabel>
										<Input size="sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("bulkPayments.searchPlaceholder")} />
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">{t("bulkPayments.status")}</FormLabel>
										<Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
											<option value="all">{t("bulkPayments.statusAll")}</option>
											<option value="unpaid">{t("bulkPayments.statusUnpaid")}</option>
											<option value="paid">{t("bulkPayments.statusPaid")}</option>
										</Select>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">{t("bulkPayments.dueFrom")}</FormLabel>
										<Input size="sm" type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} />
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">{t("bulkPayments.dueTo")}</FormLabel>
										<Input size="sm" type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)} />
									</FormControl>
								</SimpleGrid>

								<HStack spacing={6} flexWrap="wrap" align="center">
									<Checkbox isChecked={selectableOnly} onChange={(e) => setSelectableOnly(e.target.checked)}>
										{t("bulkPayments.selectableOnly")}
									</Checkbox>
									<Checkbox isChecked={hideInBatch} onChange={(e) => setHideInBatch(e.target.checked)}>
										{t("bulkPayments.hideInBatch")}
									</Checkbox>
									<Text fontSize="sm" color="gray.600">
										{t("bulkPayments.filterCount", { shown: filteredRows.length, hidden: hiddenByDeclutter })}
									</Text>
								</HStack>

								<HStack justify="space-between" flexWrap="wrap">
									<Text fontSize="sm" color="gray.600">
										{t("bulkPayments.selectionSummary", { count: selectedCount, total: selectedTotal })}
									</Text>
									<ButtonGroup size="sm">
										<Button variant="ghost" onClick={() => setStep(0)}>
											{t("bulkPayments.back")}
										</Button>
										<Button colorScheme="blue" onClick={goToReview} isDisabled={selectedCount === 0}>
											{t("bulkPayments.next")}
										</Button>
									</ButtonGroup>
								</HStack>

								<TableContainer maxH="70vh" overflowY="auto" borderWidth="1px" borderRadius="md">
									<Table size="sm">
										<Thead position="sticky" top={0} zIndex={2}>
											<Tr>
												<Th bg={tableHeaderBg}>
													<Checkbox
														isChecked={allSelectableChecked}
														isIndeterminate={!allSelectableChecked && selectableFiltered.some((row) => selectedKeys.has(row.key))}
														onChange={toggleSelectAll}
														isDisabled={selectableFiltered.length === 0}
													/>
												</Th>
												<Th bg={tableHeaderBg}>{t("bulkPayments.owner")}</Th>
												<Th bg={tableHeaderBg}>{t("bulkPayments.address")}</Th>
												<Th bg={tableHeaderBg}>{t("property.type")}</Th>
												<Th bg={tableHeaderBg}>{t("table.dueDate")}</Th>
												<Th bg={tableHeaderBg}>{t("table.amount")}</Th>
												<Th bg={tableHeaderBg}>{t("property.invoiceNumber")}</Th>
												<Th bg={tableHeaderBg}>{t("bulkPayments.notes")}</Th>
											</Tr>
										</Thead>
										<Tbody>
											{filteredRows.length === 0 ? (
												<Tr>
													<Td colSpan={8}>
														<Text textAlign="center" color="gray.500" py={4}>
															{t("bulkPayments.noPayments")}
														</Text>
													</Td>
												</Tr>
											) : (
												filteredRows.map((row) => {
													const badge = pairIndex.get(row.key);
													return (
														<Tr key={row.key} opacity={row.selectable ? 1 : 0.7}>
															<Td>
																<Checkbox
																	isChecked={selectedKeys.has(row.key)}
																	isDisabled={!row.selectable}
																	onChange={() => toggleRow(row)}
																/>
															</Td>
															<Td>{row.ownerName}</Td>
															<Td>{row.address}</Td>
															<Td>{t(`types.${row.paymentType}`, row.paymentType)}</Td>
															<Td>{row.dueDate ? moment(row.dueDate).format("DD.MM.YYYY") : "-"}</Td>
															<Td>
																{row.amount} {row.currency}
															</Td>
															<Td fontFamily="mono" fontSize="xs">
																{row.invoiceNumber || "-"}
															</Td>
															<Td>
																<VStack align="start" spacing={1}>
																	{row.reason ? (
																		<Badge colorScheme="red">{row.reason}</Badge>
																	) : (
																		<Badge colorScheme="green">{t("bulkPayments.selectable")}</Badge>
																	)}
																	{badge ? (
																		<Badge colorScheme="orange">
																			{t("bulkPayments.inBatchFrom", { date: formatBatchBadgeDate(badge.createdAt) })}
																		</Badge>
																	) : null}
																</VStack>
															</Td>
														</Tr>
													);
												})
											)}
										</Tbody>
									</Table>
								</TableContainer>
							</VStack>
						)}

						{step === 2 && (
							<VStack align="stretch" spacing={4}>
								<HStack justify="space-between" flexWrap="wrap">
									<Text fontSize="sm" color="gray.600">
										{t("bulkPayments.reviewSummary", {
											count: reviewRows.length,
											total: formatCents(reviewRows.reduce((sum, row) => sum + row.cents, 0)),
											provider: selectedProvider?.name || "-",
										})}
									</Text>
									<ButtonGroup size="sm">
										<Button variant="ghost" onClick={() => setStep(1)}>
											{t("bulkPayments.back")}
										</Button>
										<Button
											colorScheme="blue"
											onClick={handleGenerate}
											isLoading={mutationStatus === "loading"}
											isDisabled={!canGenerateReviewRows(template, reviewRows)}
										>
											{t("bulkPayments.generate")}
										</Button>
									</ButtonGroup>
								</HStack>

								<Box overflowX="auto" borderWidth="1px" borderRadius="md">
									<Table size="sm">
										<Thead>
											<Tr>
												<Th>{t("bulkPayments.address")}</Th>
												<Th>{t("table.amount")}</Th>
												{template === "associations" ? <Th>{t("bulkPayments.recipient")}</Th> : null}
												<Th>{t("bulkPayments.details1")}</Th>
												<Th>{t("bulkPayments.details2")}</Th>
												<Th>{t("bulkPayments.notes")}</Th>
											</Tr>
										</Thead>
										<Tbody>
											{reviewRows.map((row) => (
												<Tr key={row.key} bg={rowErrors[row.key] ? "red.50" : undefined}>
													<Td>
														<Text fontSize="sm">{row.address}</Text>
														<Text fontSize="xs" color="gray.500">
															{row.ownerName}
														</Text>
													</Td>
													<Td>
														{formatCents(row.cents)} RON
													</Td>
													{template === "associations" ? (
														<Td minW="220px">
															<Select
																size="sm"
																placeholder={t("bulkPayments.selectRecipient")}
																value={row.recipient ? `${row.recipient.kind}:${row.recipient.id}` : ""}
																onChange={(e) => {
																	const [kind, id] = e.target.value.split(":");
																	updateReviewRow(row.key, { recipient: kind && id ? { kind, id } : null });
																}}
															>
																{recipientOptions.map((option) => (
																	<option key={`${option.kind}:${option.id}`} value={`${option.kind}:${option.id}`}>
																		{option.kind === "association" ? t("bulkPayments.associationLabel") : t("bulkPayments.providerLabel")}:{" "}
																		{option.name}
																	</option>
																))}
															</Select>
														</Td>
													) : null}
													<Td minW="180px">
														<Input
															size="sm"
															value={row.details1}
															onChange={(e) => updateReviewRow(row.key, { details1: e.target.value })}
														/>
													</Td>
													<Td minW="180px">
														<Input
															size="sm"
															value={row.details2}
															onChange={(e) => updateReviewRow(row.key, { details2: e.target.value })}
														/>
													</Td>
													<Td>
														{rowErrors[row.key] ? <Badge colorScheme="red">{rowErrors[row.key]}</Badge> : null}
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
							</VStack>
						)}
					</TabPanel>

					<TabPanel px={0}>
						<Box overflowX="auto" borderWidth="1px" borderRadius="md">
							<Table size="sm">
								<Thead>
									<Tr>
										<Th>{t("bulkPayments.createdAt")}</Th>
										<Th>{t("bulkPayments.template")}</Th>
										<Th>{t("bulkPayments.rows")}</Th>
										<Th>{t("bulkPayments.total")}</Th>
										<Th>{t("bulkPayments.createdBy")}</Th>
										<Th></Th>
									</Tr>
								</Thead>
								<Tbody>
									{batches.length === 0 ? (
										<Tr>
											<Td colSpan={6}>
												<Text textAlign="center" color="gray.500" py={4}>
													{t("bulkPayments.noHistory")}
												</Text>
											</Td>
										</Tr>
									) : (
										batches.map((batch) => (
											<Tr key={batch.batchId}>
												<Td>{moment(batch.createdAt).format("DD.MM.YYYY HH:mm")}</Td>
												<Td>{t(`bulkPayments.templates.${batch.template}`, batch.template)}</Td>
												<Td>{batch.rowCount}</Td>
												<Td>
													{batch.total} RON
												</Td>
												<Td>
													<Text fontSize="sm">{batch.createdBy}</Text>
													<Text fontSize="xs" color="gray.500">
														{batch.createdByType}
													</Text>
												</Td>
												<Td>
													<HStack spacing={1} justify="flex-end">
														<Button size="xs" leftIcon={<FiDownload />} onClick={() => openDownload(batch.batchId)}>
															{t("bulkPayments.download")}
														</Button>
														<Button size="xs" leftIcon={<FiEye />} variant="outline" onClick={() => openView(batch.batchId)}>
															{t("bulkPayments.view")}
														</Button>
														<Button
															size="xs"
															leftIcon={<FiTrash2 />}
															colorScheme="red"
															variant="ghost"
															onClick={() => {
																setBatchToDelete(batch.batchId);
																onDeleteOpen();
															}}
														>
															{t("misc.delete")}
														</Button>
													</HStack>
												</Td>
											</Tr>
										))
									)}
								</Tbody>
							</Table>
						</Box>
					</TabPanel>
				</TabPanels>
			</Tabs>
				</CardBody>
			</Card>

			<Modal
				isOpen={isViewOpen}
				onClose={() => {
					dispatch(clearSelectedBatch());
					onViewClose();
				}}
				size="6xl"
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>
						{t("bulkPayments.batchDetails")}
						{detailStatus === "loading" ? <Spinner size="sm" ml={2} /> : null}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						{selectedBatch ? (
							<VStack align="stretch" spacing={3}>
								<Text fontSize="sm" color="gray.600">
									{t(`bulkPayments.templates.${selectedBatch.template}`)} · {selectedBatch.rowCount} · {selectedBatch.total} RON
								</Text>
								<Box overflowX="auto">
									<Table size="sm">
										<Thead>
											<Tr>
												<Th>{t("bulkPayments.contSursa")}</Th>
												<Th>{t("bulkPayments.contDestinatie")}</Th>
												<Th>{t("bulkPayments.suma")}</Th>
												<Th>{t("bulkPayments.beneficiar")}</Th>
												<Th>{t("bulkPayments.details1")}</Th>
												<Th>{t("bulkPayments.details2")}</Th>
											</Tr>
										</Thead>
										<Tbody>
											{(selectedBatch.rows || []).map((row, index) => (
												<Tr key={`${row.paymentId}-${index}`}>
													<Td fontFamily="mono" fontSize="xs">
														{row.contSursa}
													</Td>
													<Td fontFamily="mono" fontSize="xs">
														{row.contDestinatie}
													</Td>
													<Td>{row.suma}</Td>
													<Td>{row.beneficiar}</Td>
													<Td>{row.detalii1}</Td>
													<Td>{row.detalii2}</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
							</VStack>
						) : null}
					</ModalBody>
				</ModalContent>
			</Modal>

			<PropertyConfirmModal
				isOpen={isDeleteOpen}
				onClose={() => {
					setBatchToDelete(null);
					onDeleteClose();
				}}
				onConfirm={confirmDelete}
				title={t("property.confirmDelete")}
				message={t("bulkPayments.deleteConfirmation")}
				emphasis={t("property.actionCannotBeUndone")}
				isLoading={mutationStatus === "loading"}
			/>
		</Flex>
	);
}
